import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPage() {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Privacy Policy',
      lastUpdate: 'Last updated: March 2026',
      sections: [
        {
          title: '1. Introduction',
          content: `BidTrip ("we", "our", or "the Platform") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our marketplace platform for booking experiences in Mexico.

By using BidTrip, you consent to the data practices described in this policy.`
        },
        {
          title: '2. Information We Collect',
          content: `2.1. Personal Information:
• Name and surname
• Email address
• Phone number (optional)
• Profile photo (optional)
• Bank account information (for Providers and Partners)

2.2. Transaction Information:
• Booking history
• Payment information (processed securely via Stripe)
• Communication history with Providers

2.3. Automatically Collected Information:
• Device information (browser type, operating system)
• IP address
• Usage data (pages visited, features used)
• Cookies and similar technologies`
        },
        {
          title: '3. How We Use Your Information',
          content: `We use the collected information for:
• Creating and managing your account
• Processing bookings and payments
• Facilitating communication between Users and Providers
• Providing customer support
• Sending booking confirmations and updates
• Improving our services and user experience
• Preventing fraud and ensuring platform security
• Complying with legal obligations
• Sending promotional communications (with your consent)`
        },
        {
          title: '4. Information Sharing',
          content: `4.1. With Providers:
When you make a booking, we share necessary information with the Provider, including your name, contact details, and booking specifics.

4.2. With Payment Processors:
We use Stripe to process payments. Your payment information is handled directly by Stripe according to their privacy policy.

4.3. With Partners:
If you registered using a referral code, the Partner may see limited information about your bookings for commission purposes.

4.4. Legal Requirements:
We may disclose your information if required by law or to protect our rights and safety.

4.5. We DO NOT sell your personal information to third parties.`
        },
        {
          title: '5. Data Security',
          content: `We implement appropriate security measures to protect your information:
• SSL encryption for data transmission
• Secure password hashing
• Regular security audits
• Limited access to personal data
• Secure payment processing via Stripe

However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security of your data.`
        },
        {
          title: '6. Data Retention',
          content: `We retain your personal information for as long as:
• Your account is active
• Necessary to provide our services
• Required by applicable laws
• Needed for legitimate business purposes

You may request deletion of your account and data by contacting us at legal@bidtrip.live.`
        },
        {
          title: '7. Your Rights',
          content: `You have the right to:
• Access your personal data
• Correct inaccurate information
• Request deletion of your data
• Object to data processing
• Data portability
• Withdraw consent at any time

To exercise these rights, contact us at legal@bidtrip.live.`
        },
        {
          title: '8. Cookies',
          content: `BidTrip uses cookies and similar technologies to:
• Keep you logged in
• Remember your preferences (language, etc.)
• Analyze platform usage
• Improve user experience

You can control cookies through your browser settings. Disabling cookies may affect platform functionality.`
        },
        {
          title: '9. Third-Party Services',
          content: `Our platform may contain links to third-party websites or use third-party services:
• Stripe (payments)
• OpenAI (AI assistant)
• SendGrid (email communications)

These services have their own privacy policies, and we encourage you to review them.`
        },
        {
          title: '10. Children\'s Privacy',
          content: `BidTrip is not intended for users under 18 years of age. We do not knowingly collect information from children. If you believe a child has provided us with personal information, please contact us immediately.`
        },
        {
          title: '11. International Data Transfers',
          content: `Your information may be transferred to and processed in countries other than Mexico. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.`
        },
        {
          title: '12. Changes to This Policy',
          content: `We may update this Privacy Policy from time to time. We will notify you of significant changes via email or platform notification. The "Last updated" date at the top indicates when the policy was last revised.`
        },
        {
          title: '13. Contact Us',
          content: `For questions or concerns about this Privacy Policy or our data practices:
• Email: legal@bidtrip.live
• Support: soporte@bidtrip.live

Data Controller: BidTrip
Location: Mexico`
        }
      ]
    },
    es: {
      title: 'Política de Privacidad',
      lastUpdate: 'Última actualización: Marzo 2026',
      sections: [
        {
          title: '1. Introducción',
          content: `BidTrip ("nosotros", "nuestro" o "la Plataforma") está comprometido con proteger tu privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos tu información cuando utilizas nuestra plataforma de marketplace para reservar experiencias en México.

Al usar BidTrip, consientes las prácticas de datos descritas en esta política.`
        },
        {
          title: '2. Información que Recopilamos',
          content: `2.1. Información Personal:
• Nombre y apellido
• Dirección de correo electrónico
• Número de teléfono (opcional)
• Foto de perfil (opcional)
• Información bancaria (para Proveedores y Partners)

2.2. Información de Transacciones:
• Historial de reservaciones
• Información de pago (procesada de forma segura vía Stripe)
• Historial de comunicaciones con Proveedores

2.3. Información Recopilada Automáticamente:
• Información del dispositivo (tipo de navegador, sistema operativo)
• Dirección IP
• Datos de uso (páginas visitadas, funciones utilizadas)
• Cookies y tecnologías similares`
        },
        {
          title: '3. Cómo Usamos Tu Información',
          content: `Usamos la información recopilada para:
• Crear y administrar tu cuenta
• Procesar reservaciones y pagos
• Facilitar la comunicación entre Usuarios y Proveedores
• Proporcionar soporte al cliente
• Enviar confirmaciones de reservación y actualizaciones
• Mejorar nuestros servicios y experiencia de usuario
• Prevenir fraudes y garantizar la seguridad de la plataforma
• Cumplir con obligaciones legales
• Enviar comunicaciones promocionales (con tu consentimiento)`
        },
        {
          title: '4. Compartir Información',
          content: `4.1. Con Proveedores:
Cuando haces una reservación, compartimos información necesaria con el Proveedor, incluyendo tu nombre, datos de contacto y detalles de la reservación.

4.2. Con Procesadores de Pago:
Usamos Stripe para procesar pagos. Tu información de pago es manejada directamente por Stripe según su política de privacidad.

4.3. Con Partners:
Si te registraste usando un código de referido, el Partner puede ver información limitada sobre tus reservaciones para propósitos de comisiones.

4.4. Requisitos Legales:
Podemos divulgar tu información si es requerido por ley o para proteger nuestros derechos y seguridad.

4.5. NO vendemos tu información personal a terceros.`
        },
        {
          title: '5. Seguridad de Datos',
          content: `Implementamos medidas de seguridad apropiadas para proteger tu información:
• Encriptación SSL para transmisión de datos
• Hash seguro de contraseñas
• Auditorías de seguridad regulares
• Acceso limitado a datos personales
• Procesamiento seguro de pagos vía Stripe

Sin embargo, ningún método de transmisión por Internet es 100% seguro. No podemos garantizar la seguridad absoluta de tus datos.`
        },
        {
          title: '6. Retención de Datos',
          content: `Retenemos tu información personal mientras:
• Tu cuenta esté activa
• Sea necesario para proporcionar nuestros servicios
• Sea requerido por las leyes aplicables
• Sea necesario para propósitos comerciales legítimos

Puedes solicitar la eliminación de tu cuenta y datos contactándonos en legal@bidtrip.live.`
        },
        {
          title: '7. Tus Derechos',
          content: `Tienes derecho a:
• Acceder a tus datos personales
• Corregir información inexacta
• Solicitar la eliminación de tus datos
• Oponerte al procesamiento de datos
• Portabilidad de datos
• Retirar tu consentimiento en cualquier momento

Para ejercer estos derechos, contáctanos en legal@bidtrip.live.`
        },
        {
          title: '8. Cookies',
          content: `BidTrip usa cookies y tecnologías similares para:
• Mantener tu sesión iniciada
• Recordar tus preferencias (idioma, etc.)
• Analizar el uso de la plataforma
• Mejorar la experiencia del usuario

Puedes controlar las cookies a través de la configuración de tu navegador. Deshabilitar las cookies puede afectar la funcionalidad de la plataforma.`
        },
        {
          title: '9. Servicios de Terceros',
          content: `Nuestra plataforma puede contener enlaces a sitios web de terceros o usar servicios de terceros:
• Stripe (pagos)
• OpenAI (asistente de IA)
• SendGrid (comunicaciones por correo)

Estos servicios tienen sus propias políticas de privacidad, y te animamos a revisarlas.`
        },
        {
          title: '10. Privacidad de Menores',
          content: `BidTrip no está destinado a usuarios menores de 18 años. No recopilamos intencionalmente información de menores. Si crees que un menor nos ha proporcionado información personal, contáctanos inmediatamente.`
        },
        {
          title: '11. Transferencias Internacionales de Datos',
          content: `Tu información puede ser transferida y procesada en países distintos a México. Aseguramos que existan salvaguardas apropiadas para dichas transferencias en cumplimiento con las leyes de protección de datos aplicables.`
        },
        {
          title: '12. Cambios a Esta Política',
          content: `Podemos actualizar esta Política de Privacidad de vez en cuando. Te notificaremos de cambios significativos por correo electrónico o notificación en la plataforma. La fecha de "Última actualización" en la parte superior indica cuándo se revisó la política por última vez.`
        },
        {
          title: '13. Contáctanos',
          content: `Para preguntas o inquietudes sobre esta Política de Privacidad o nuestras prácticas de datos:
• Correo: legal@bidtrip.live
• Soporte: soporte@bidtrip.live

Responsable de Datos: BidTrip
Ubicación: México`
        }
      ]
    }
  };

  const t = content[language] || content.es;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="privacy-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={24} />
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="text-caribbean" size={24} />
            <h1 className="font-heading text-xl font-bold">{t.title}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold">
              Bid<span className="text-caribbean">Trip</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-2">{t.lastUpdate}</p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <section key={index} className="prose prose-slate max-w-none">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {section.title}
                </h3>
                <div className="text-muted-foreground whitespace-pre-line text-sm leading-relaxed">
                  {section.content}
                </div>
              </section>
            ))}
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t flex flex-wrap gap-4 justify-center text-sm">
            <Link to="/terms" className="text-caribbean hover:underline">
              {language === 'en' ? 'Terms and Conditions' : 'Términos y Condiciones'}
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/provider-terms" className="text-caribbean hover:underline">
              {language === 'en' ? 'Provider Terms' : 'Términos para Proveedores'}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
